import _ from 'lodash';
import block from 'bem-cn-lite';
import React, {RefObject, createRef, Fragment} from 'react';

import ToggleArrow from 'components/ToggleArrow/ToggleArrow';
import {NavigationConfig, NavigationSection} from 'units/navigation/models';
import NavigationPopup from 'units/navigation/components/NavigationPopup/NavigationPopup';
import OverflowScroller from 'units/navigation/components/OverflowScroller/OverflowScroller';
import withRouter, {WithRouterProps} from 'hoc/withRouter';

import './Navigation.scss';

const b = block('navigation');

export interface NavigationOwnProps {
    config: NavigationConfig;
    activeItemIndex: number;
    onActiveItemChange: (index: number) => void;
    className?: string;
}

export type NavigationProps = NavigationOwnProps & WithRouterProps;

type LinkClickHandler = () => void;

interface NavigationState {
    itemPositions: number[];
}

class Navigation extends React.Component<NavigationProps, NavigationState> {
    itemRefs: RefObject<HTMLLIElement>[] = [];
    state = {
        itemPositions: [],
    };
    lastLeftScroll = 0;

    componentDidMount() {
        this.calculateItemPositions();
        this.lastLeftScroll = window.pageXOffset;

        this.props.router.events?.on('pageHeaderHidden', this.hidePopup);

        window.addEventListener('resize', this.calculateItemPositions);
        window.addEventListener('scroll', this.calculateOnScroll);
    }

    componentWillUnmount() {
        this.props.router.events?.off('pageHeaderHidden', this.hidePopup);

        window.removeEventListener('resize', this.calculateItemPositions);
        window.removeEventListener('scroll', this.calculateOnScroll);
    }

    render() {
        const {config, className, activeItemIndex, onActiveItemChange} = this.props;
        const {itemPositions} = this.state;

        return (
            <OverflowScroller
                className={b(null, className)}
                onScrollStart={this.hidePopup}
                onScrollEnd={this.calculateItemPositions}
            >
                <nav>
                    <ul className={b('links')}>
                        {config.map((section, index) => {
                            const isActive = index === activeItemIndex;
                            const onClick = () => onActiveItemChange(isActive ? -1 : index);

                            if (!this.itemRefs[index]) {
                                this.itemRefs[index] = createRef();
                            }

                            return (
                                <li
                                    ref={this.itemRefs[index]}
                                    key={index}
                                    className={b('links-item')}
                                >
                                    {section.dropdown === undefined
                                        ? this.renderNavLink(section, onClick)
                                        : this.renderNavDropdown(
                                              section,
                                              onClick,
                                              isActive,
                                              itemPositions[index],
                                          )}
                                    {isActive && this.renderSlider()}
                                </li>
                            );
                        })}
                    </ul>
                </nav>
            </OverflowScroller>
        );
    }

    renderNavLink = ({title, url}: NavigationSection, onClick: LinkClickHandler) => {
        return (
            <a href={url} title={title} className={b('link')} onClick={onClick}>
                <span className={b('link-text')}>{title}</span>
            </a>
        );
    };

    renderNavDropdown = (
        data: NavigationSection,
        onClick: LinkClickHandler,
        isActive: boolean,
        position: number,
    ) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {title, url, columns, ...popupProps} = data;

        return (
            <Fragment>
                <div
                    className={b('link')}
                    onClick={onClick}
                    data-qa={data.type && `${data.type}-navigation-button`}
                >
                    <span className={b('link-text')}>{title}</span>
                    <ToggleArrow
                        className={b('arrow')}
                        size={12}
                        type={'vertical'}
                        iconType="navigation"
                        open={isActive}
                    />
                </div>
                {isActive && (
                    <NavigationPopup
                        left={position}
                        onClose={this.hidePopup}
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        columns={columns!}
                        dataQa={data.type && `${data.type}`}
                        {...popupProps}
                    />
                )}
            </Fragment>
        );
    };

    renderSlider() {
        return (
            <div className={b('slider-container')}>
                <div className={b('slider')} />
            </div>
        );
    }

    hidePopup = () => {
        this.props.onActiveItemChange(-1);
    };

    // eslint-disable-next-line react/sort-comp, @typescript-eslint/member-ordering
    calculateItemPositions = _.debounce(() => {
        if (this.itemRefs.length) {
            const itemPositions = this.itemRefs.map(
                (itemRef) => (itemRef.current && itemRef.current.getBoundingClientRect().left) || 0,
            );
            this.setState({itemPositions});
        }
    }, 100);

    // eslint-disable-next-line react/sort-comp, @typescript-eslint/member-ordering
    calculateOnScroll = _.debounce(() => {
        const curLeftScroll = window.pageXOffset;

        if (curLeftScroll !== this.lastLeftScroll) {
            this.lastLeftScroll = curLeftScroll;
            this.calculateItemPositions();
        }
    }, 100);
}

export default withRouter(Navigation);
